import { Button, FormFeedback, FormGroup, Input } from "reactstrap";
import { OperationResult } from "urql";
import { CSSProperties, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { useAutoSaveMutation } from "./useAutoSaveMutation";
import { SaveStatusIndicator } from "./SaveStatusIndicator";

/**
 * Textarea input component with GraphQL mutation support.
 *
 * This component accepts strictly typed values (string | null) and does not accept undefined.
 * This enforces explicit handling of optional fields at the call site.
 *
 * ## validateAndTransform Usage
 *
 * Return values:
 * - **string**: Valid, transformed value (e.g., trimmed, normalized)
 * - **null**: Empty value is acceptable
 * - **'invalid'**: Validation failed, value is not acceptable
 *
 * **Important:** For empty strings, `validateAndTransform` should return `null`,
 * not an empty string `""`. Empty strings should be normalized to `null` to clearly indicate "no value".
 *
 * @example
 * // Optional field - empty strings → null
 * <TextareaInputGraphQlMutate
 *     value={song.chordsheet ?? null}
 *     validateAndTransform={(value) => {
 *         if (value.trim().length === 0) return null;  // ✓ Return null, not ""
 *         return value.trim();
 *     }}
 * />
 *
 * @example
 * // For optional fields from GraphQL, use nullish coalescing:
 * <TextareaInputGraphQlMutate value={song.chordsheet ?? null} ... />
 */

interface Props<
  TData = unknown,
  TVariables extends Record<string, unknown> = Record<string, unknown>,
> {
  htmlTagIdAndName: string;
  /** Value must be string or null. Use nullish coalescing for optional fields: value={song.chordsheet ?? null} */
  value: string | null;
  mutationDocument: TypedDocumentNode<TData, TVariables>;
  buildVariables: (validatedValue: string | null) => TVariables;
  /**
   * Custom validation and transformation function.
   * @param newValue - The current input value
   * @returns string (valid value), null (empty/cleared value), or 'invalid' (validation failed)
   */
  validateAndTransform?: (newValue: string) => string | null | "invalid";
  invalidFeedback?: string;
  autoFocus?: boolean;
  isDisabled?: boolean;
  onMutationCompleted?: (r: OperationResult<TData, TVariables>) => void;
  style?: CSSProperties | undefined;
  className?: string | undefined;
  placeholder?: string;
  setContentPreview?: (value: string | null) => void;
}

const defaultValidateAndTransform = (
  newValue: string,
): string | null | "invalid" => {
  if (newValue.trim().length === 0) {
    return null;
  }
  return newValue.trim();
};

export function TextareaInputGraphQlMutate<
  TData = unknown,
  TVariables extends Record<string, unknown> = Record<string, unknown>,
>(props: Props<TData, TVariables>) {
  const {
    htmlTagIdAndName,
    value,
    mutationDocument,
    buildVariables,
    validateAndTransform,
    invalidFeedback = "Das Feld ist ungültig.",
    autoFocus = false,
    isDisabled = false,
    onMutationCompleted = () => {},
    style,
    className,
    placeholder,
    setContentPreview,
  } = props;

  const internalValidateAndTransform = useMemo(
    () => validateAndTransform || defaultValidateAndTransform,
    [validateAndTransform],
  );

  const {
    inputValue,
    setInputValue,
    isValid,
    isFocused,
    inputRef,
    handleFocus,
    handleBlur,
    handleSave,
    handleCancel,
    saveStatus,
  } = useAutoSaveMutation({
    value,
    mutationDocument,
    buildVariables,
    validateAndTransform: internalValidateAndTransform,
    onMutationCompleted,
    hotkey: "shift+enter",
    hotkeyFormTag: "textarea",
    onContentPreviewUpdate: setContentPreview,
  });

  return (
    <FormGroup>
      <Input
        type="textarea"
        innerRef={inputRef}
        id={htmlTagIdAndName}
        name={htmlTagIdAndName}
        style={style}
        className={className}
        placeholder={placeholder}
        value={inputValue}
        onChange={(evt) => setInputValue(evt.currentTarget.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        invalid={!isValid}
        autoFocus={autoFocus}
        disabled={isDisabled}
      />
      <FormFeedback>{invalidFeedback}</FormFeedback>

      <div className="d-flex gap-2 mt-2">
        <Button
          disabled={!isFocused}
          color="primary"
          tabIndex={-1}
          onMouseDown={(e) => {
            e.preventDefault(); // Prevent blur event
            handleSave();
          }}
        >
          <FontAwesomeIcon icon={faCheck} />
        </Button>
        <Button
          disabled={!isFocused}
          color="primary"
          tabIndex={-1}
          onMouseDown={(e) => {
            e.preventDefault(); // Prevent blur event
            handleCancel();
          }}
        >
          <FontAwesomeIcon icon={faTimes} />
        </Button>
        <SaveStatusIndicator
          htmlTagIdAndName={htmlTagIdAndName}
          status={saveStatus}
        />
      </div>
    </FormGroup>
  );
}
